import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { BarChart2, Edit3 } from 'lucide-react';
import clsx from 'clsx';
import { MassiveUploadPanel } from './components/MassiveUploadPanel';
import { ManualOrderForm } from './components/ManualOrderForm';
import { UPLOAD_MODES } from '../../../../features/deliveries/orderUploadConstants';
import { selectCountryIso2 } from '../../../../features/auth/authSelectors';

const HOW_IT_WORKS_STEPS = [
  {
    title: 'Descarga la plantilla',
    description: 'Usa nuestra plantilla para asegurar que los datos esten en formato correcto.',
  },
  {
    title: 'Llena los datos',
    description:
      'Completa las columnas: destinatario, direccion, comuna, telefono, SKU y cantidad.',
  },
  {
    title: 'Sube el archivo',
    description:
      'Arrastra o selecciona el archivo. El sistema procesara cada orden automaticamente.',
  },
  {
    title: 'Revisa y confirma',
    description: 'Verifica que no haya errores y confirma la creacion de las ordenes.',
  },
];

/**
 * Order upload for a seller: always into one of its own warehouses. The
 * country, which decides the template, the address fields and the tax id,
 * comes from the seller's company.
 */
export const Upload = () => {
  const [activeMode, setActiveMode] = useState(UPLOAD_MODES.MASSIVE);
  const country = useSelector(selectCountryIso2);

  return (
    <div data-testid="upload-page" className="max-w-5xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => setActiveMode(UPLOAD_MODES.MASSIVE)}
          className={clsx(
            "p-8 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-4 cursor-pointer text-center",
            activeMode === UPLOAD_MODES.MASSIVE
              ? "border-ninja-green bg-ninja-greenLight"
              : "border-border bg-white dark:bg-[#1a1a1a] hover:border-ninja-green hover:bg-ninja-greenLight"
          )}
        >
          <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center">
            <BarChart2 size={32} className="text-ninja-green" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-ninja-dark dark:text-white mb-2">Carga Masiva</h3>
            <p className="text-sm text-text-secondary">Subi un archivo Excel o CSV con multiples ordenes. Ideal para grandes volumenes.</p>
          </div>
        </button>

        <button
          onClick={() => setActiveMode(UPLOAD_MODES.MANUAL)}
          className={clsx(
            "p-8 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-4 cursor-pointer text-center",
            activeMode === UPLOAD_MODES.MANUAL
              ? "border-ninja-green bg-ninja-greenLight"
              : "border-border bg-white dark:bg-[#1a1a1a] hover:border-ninja-green hover:bg-ninja-greenLight"
          )}
        >
          <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center">
            <Edit3 size={32} className="text-warning" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-ninja-dark dark:text-white mb-2">Carga Manual</h3>
            <p className="text-sm text-text-secondary">Crea ordenes una por una de forma individual. Ideal para envios urgentes.</p>
          </div>
        </button>
      </div>

      {activeMode === UPLOAD_MODES.MASSIVE && (
        <>
          <MassiveUploadPanel country={country} />

          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 shadow-sm border border-border mt-8">
            <h3 className="text-lg font-bold text-ninja-dark dark:text-white flex items-center gap-2 mb-6">
              <span className="text-xl">📚</span> Como funciona la carga masiva
            </h3>

            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-4 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent">
              {HOW_IT_WORKS_STEPS.map((step, index) => (
                <div
                  key={step.title}
                  className={clsx(
                    'relative flex items-start gap-6',
                    index > 0 && 'border-t border-border pt-6'
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-ninja-greenLight text-ninja-green font-bold flex items-center justify-center shrink-0 z-10 border-2 border-white dark:border-[#1a1a1a]">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-ninja-dark dark:text-white mb-1">{step.title}</h4>
                    <p className="text-sm text-text-secondary">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeMode === UPLOAD_MODES.MANUAL && <ManualOrderForm country={country} />}
    </div>
  );
};
